import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { BASE_URL } from "../../global";
import ExcelUpload from "../../utils/components/excelUpload";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  height: "70vh",
  backgroundColor: "#fff",
  boxShadow: 24,
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  p: 4,
};

// handles Date objects, Excel serial numbers, and common string formats like DD/MM/YYYY
const parseExcelDate = (raw) => {
  if (!raw && raw !== 0) return null;

  if (raw instanceof Date) {
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${raw.getFullYear()}-${m}-${d}`;
  }

  if (typeof raw === "number") {
    const d = XLSX.SSF.parse_date_code(raw);
    if (d && d.y > 1900) {
      return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
    }
    return null;
  }

  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s || s === "-") return null;

    const ddmm = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmm) return `${ddmm[3]}-${ddmm[2].padStart(2, "0")}-${ddmm[1].padStart(2, "0")}`;

    const ddmmDash = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (ddmmDash) return `${ddmmDash[3]}-${ddmmDash[1].padStart(2, "0")}-${ddmmDash[2].padStart(2, "0")}`;

    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }

  return null;
};

// client column format is "id, Company Name" 
const parseClientId = (raw) => {
  if (!raw) return null;
  const id = parseInt(String(raw).split(",")[0].trim(), 10);
  return isNaN(id) ? null : id;
};

// treat empty strings and dashes as null
const normOptional = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s === "" || s === "-" ? null : s;
};

export const ImportIndependentDueModal = ({ onImportSuccess }) => {
  const [open, setOpen]               = useState(false);
  const [excelArray, setExcelArray]   = useState([]);
  const [clients, setClients]         = useState([]);
  const [rejectedData, setRejectedData]   = useState([]);
  const [remainingData, setRemainingData] = useState([]);
  const [saving, setSaving]           = useState(false);

  const handleOpen  = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setExcelArray([]);
    setRejectedData([]);
    setRemainingData([]);
  };

  const fetchClientsData = () => {
    axiosWithToken
      .get(BASE_URL + "clients?_where=(status,eq,1)&_fields=id,companyName")
      .then((res) => setClients(res.data || []))
      .catch((err) => console.error("Failed to fetch clients", err));
  };

  useEffect(() => { fetchClientsData(); }, []);

  useEffect(() => {
    if (!excelArray.length) {
      setRejectedData([]);
      setRemainingData([]);
      return;
    }

    const accepted = [];
    const rejected = [];

    const nonEmptyRows = excelArray.filter((row) =>
      Object.values(row).some((v) => v !== undefined && v !== null && v !== "")
    );

    nonEmptyRows.forEach((row, i) => {
      const rowNumber = i + 2;

      const rawClient  = row["Clients"]                ?? row["Client"]           ?? null;
      const rawSRN     = row["Service Request Number"] ?? row["serviceReqNumber"] ?? row["Service Req Number"] ?? null;
      const rawQty     = row["Quantity"]               ?? row["quantity"]         ?? null;
      const rawDueDate = row["Due Date"]               ?? row["dueDate"]          ?? row["DueDate"] ?? null;

      const clientId     = parseClientId(rawClient);
      const clientRecord = clientId ? clients.find((c) => c.id === clientId) : null;

      if (!clientId) {
        rejected.push({
          rowNumber,
          rawClient:  String(rawClient  ?? ""),
          rawSRN:     String(rawSRN     ?? ""),
          rawQty:     String(rawQty     ?? ""),
          rawDueDate: String(rawDueDate ?? ""),
          rejectionStatus: "Invalid client — expected format: id, Company Name (e.g. 4, Ace Designers Limited)",
        });
        return;
      }

      if (!clientRecord) {
        rejected.push({
          rowNumber,
          rawClient:  String(rawClient  ?? ""),
          rawSRN:     String(rawSRN     ?? ""),
          rawQty:     String(rawQty     ?? ""),
          rawDueDate: String(rawDueDate ?? ""),
          rejectionStatus: `Client ID ${clientId} not found in database`,
        });
        return;
      }

      const dueDate = parseExcelDate(rawDueDate);
      if (!dueDate) {
        rejected.push({
          rowNumber,
          rawClient:  String(rawClient  ?? ""),
          rawSRN:     String(rawSRN     ?? ""),
          rawQty:     String(rawQty     ?? ""),
          rawDueDate: String(rawDueDate ?? ""),
          rejectionStatus: "Invalid or missing Due Date — enter as text DD/MM/YYYY (e.g. 31/12/2026)",
        });
        return;
      }

      const serviceRequestNo = normOptional(rawSRN);

      let quantity = null;
      const normQty = normOptional(rawQty !== null ? String(rawQty) : null);
      if (normQty !== null) {
        quantity = parseInt(normQty, 10);
        if (isNaN(quantity) || quantity < 1) quantity = null;
      }

      accepted.push({ clientId, clientName: clientRecord.companyName, serviceRequestNo, quantity, dueDate });
    });

    setRejectedData(rejected);
    setRemainingData(accepted);
  }, [excelArray, clients]);

  const handleSubmit = async () => {
    setSaving(true);
    const payloads = remainingData.map((r) => ({
      clientId:         r.clientId,
      serviceRequestNo: r.serviceRequestNo ?? null,
      quantity:         r.quantity         ?? null,
      dueDate:          r.dueDate,
    }));

    try {
      await Promise.all(
        payloads.map((row) => axiosWithToken.post(BASE_URL + "independentDueList", row))
      );
      toast.success(`${payloads.length} records imported successfully!`);
      if (onImportSuccess) onImportSuccess();
      handleClose();
    } catch (err) {
      toast.error("Import failed: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Button variant="contained" size="small" onClick={handleOpen}>
        Upload Excel File
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">Upload Excel for Independent Due</Typography>

            <Typography
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                px: 2, py: 0.5,
                borderRadius: "12px",
                fontSize: "13px",
                mr: 1,
              }}
            >
              Accepted: {remainingData.length} | Rejected: {rejectedData.length}
            </Typography>

            <Button
              onClick={handleClose}
              sx={{
                minWidth: "30px", width: "30px", height: "30px",
                borderRadius: "50%",
                fontSize: "18px", fontWeight: "bold",
                color: "#fff",
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              X
            </Button>
          </Box>

          <ExcelUpload setExcelArray={setExcelArray} buttonName="Select Excel File" />

          <Typography textAlign="center" margin="15px 0">
            Rejected Excel Data
          </Typography>

          <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><b>Sr.</b></TableCell>
                  <TableCell><b>Client</b></TableCell>
                  <TableCell><b>Service Req. No.</b></TableCell>
                  <TableCell><b>Quantity</b></TableCell>
                  <TableCell><b>Due Date</b></TableCell>
                  <TableCell><b>Rejection Status</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rejectedData.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.rowNumber}</TableCell>
                    <TableCell>{row.rawClient}</TableCell>
                    <TableCell>{row.rawSRN}</TableCell>
                    <TableCell>{row.rawQty}</TableCell>
                    <TableCell>{row.rawDueDate}</TableCell>
                    <TableCell sx={{ color: "#d32f2f" }}>{row.rejectionStatus}</TableCell>
                  </TableRow>
                ))}
                {rejectedData.length === 0 && excelArray.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "success.main" }}>
                      All rows accepted ✓
                    </TableCell>
                  </TableRow>
                )}
                {excelArray.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "text.secondary" }}>
                      No file selected yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
            <Button variant="contained" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!remainingData.length || saving}
              onClick={handleSubmit}
            >
              {saving ? "Saving..." : "Continue"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};
